import { Box, BoxProps, Checkbox, CheckboxGroup } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { EntityReferenceModelGroup } from "../shared/temp/EntityReferenceModelGroup";
import { EntityReferenceModel } from "../clients/KompasApi";

type MultiselectItem<T extends EntityReferenceModel> =
  | T
  | EntityReferenceModelGroup<T>;

const flatten = <T extends EntityReferenceModel>(
  items: Array<MultiselectItem<T>>
): T[] =>
  items.flatMap((item) =>
    item instanceof EntityReferenceModelGroup ? flatten(item.children) : item
  );

interface Props<T extends EntityReferenceModel> {
  allItems?: T[];
  items: MultiselectItem<T>[];
  onChange?: (selectedItems: T[]) => void;
  onToggle?: (item: T) => void;
  value: T[];
  maxH?: BoxProps["maxH"];
}

export function Multiselect<T extends EntityReferenceModel>({
  allItems,
  items,
  onChange,
  onToggle,
  value,
  maxH,
}: Props<T>) {
  const memoizedAllItems = useMemo(
    () => allItems ?? flatten(items),
    [allItems, items]
  );

  const toggleItem = useCallback(
    (item: T) => {
      const selected = new Set(value.map((i) => i.id));

      if (selected.has(item.id)) {
        selected.delete(item.id);
      } else {
        selected.add(item.id);
      }

      onToggle?.(item);
      onChange?.(memoizedAllItems.filter((i) => selected.has(i.id)));
    },
    [value, onChange, memoizedAllItems, onToggle]
  );

  return (
    <Box maxH={maxH} overflowY="auto">
      <CheckboxGroup>
        {items.map((item) =>
          item instanceof EntityReferenceModelGroup ? (
            <Box key={`group-${item.name}`} mt={2}>
              {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
              <MultiselectGroup
                allItems={memoizedAllItems}
                group={item}
                onChange={onChange}
                toggleItem={toggleItem}
                value={value}
              />
            </Box>
          ) : (
            <Box key={`item-${item.name}`}>
              <Checkbox
                isChecked={value.includes(item)}
                onChange={() => toggleItem(item)}
                width="100%"
              >
                {item.name}
              </Checkbox>
            </Box>
          )
        )}
      </CheckboxGroup>
    </Box>
  );
}

function MultiselectGroup<T extends EntityReferenceModel>({
  group,
  onChange,
  toggleItem,
  allItems,
  value,
}: {
  group: EntityReferenceModelGroup<T>;
  onChange?: (selectedItems: T[]) => void;
  toggleItem: (item: T) => void;
  allItems: T[];
  value: T[];
}) {
  const allChildren = useMemo(() => flatten([group]), [group]);
  const allSelected = allChildren.every((child) => value.includes(child));
  const someSelected = allChildren.some((child) => value.includes(child));

  const toggleGroup = useCallback(() => {
    const selected = new Set(value.map((i) => i.id));

    allChildren.forEach((child) => {
      if (allSelected) {
        selected.delete(child.id);
        toggleItem(child);
      } else if (selected.add(child.id)) {
        toggleItem(child);
      }
    });

    onChange?.(allItems.filter((i) => selected.has(i.id)));
  }, [value, onChange, allItems, toggleItem, allChildren, allSelected]);

  return (
    <CheckboxGroup>
      <Box fontWeight="bold">
        <Checkbox
          isChecked={allSelected}
          isIndeterminate={someSelected && !allSelected}
          onChange={toggleGroup}
        />
        {group.name}
      </Box>
      <Box ml="19px">
        <Multiselect<T>
          allItems={allItems}
          items={group.children}
          onChange={onChange}
          onToggle={toggleItem}
          value={value}
        />
      </Box>
    </CheckboxGroup>
  );
}
